import React, {
  FunctionComponent,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  Choice,
  ChoiceList,
  CloseIconThin,
  HelpDrawer,
  SkipNav,
} from "@cmsgov/ds-medicare-gov";
import { FormattedResultsMessage, PharmacyDrugCosts, PharmacyCard } from "./";
import {
  CONSTANTS,
  fetchCosts,
  IntlContext,
  useTranslate,
} from "../../../helpers";
import filterIcon from "./../../../assets/filter.svg";
import mapIcon from "./../../../assets/map.svg";
import { DrugCostInfo, PharmacyResultsType, Plan } from "../../../@types";
import { ToggledFrom } from "../types";
import { normalizeStringCase } from "../helpers";
import Pagination from "../../../components/Pagination";
import PharmacySelectionMap from "../../../components/PharmacySelectionMap";
import {
  CCXPDropdown,
  InNetworkLabel,
  OutOfNetworkLabel,
} from "../../../components";
import {
  baseAddPharmacyClassName,
  labelIdSuffix,
  nameIdSuffix,
} from "./constants";
import { PharmacyCardProps } from "./PharmacyCard";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../../../app/store";
import classNames from "classnames";
import { AlertMessage } from "../../../components";
import { alertMessageHierarchy } from "../../../app/contexts/AlertMessaging/constants";
import { useAlertMessaging } from "../../../app/contexts";
import { getMessageToShow } from "../../../app/contexts/AlertMessaging/helpers";
import { AlertMessageHierarchyKeys } from "../../../app/contexts/AlertMessaging/types";
import InformationIcon from "../../../assets/information.svg";
import { PharmacyNetworkLabelHelpDrawer } from "../../../components/HelpDrawers/PharmacyNetworkLabelHelpDrawer";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";

export type ResultsContainerProps = {
  handlePreferredOnlyClick: (showPreferred: boolean) => void;
  loadPharmacies: (params: {
    radius_miles?: number;
    pageNumber?: number;
    /**
     * @property {calculateAverageDistance} - If set to `true`, calculations will
     * be run to find the average distance to pharmacies. That distance will then
     * be used to programatically fetch pharmacies a second time.
     */
    calculateAverageDistance?: boolean;
    filterByPreferred?: boolean;
  }) => Promise<void>;
  setFooterMessage: (message: string) => void;
  setMobileSearchDistance: (mobileSearchDistance: number) => void;
  setPage: (page: number) => void;
  setSearchDistance: (distance: number) => void;
  setShowMap: (shouldShowMap: boolean) => void;
  setShowMobileFilter: (shouldShowMobileFilter: boolean) => void;
  toggleMailOrderPharmacy: (isMailOrder: boolean) => void;
  togglePharmacy: (
    toggledFrom?: ToggledFrom
  ) => (npi: string | number, pharmacyNumber: number) => void;
  isInNetwork: boolean;
  isMailOrderInNetwork: boolean;
  isMailOrderPreferred: boolean;
  loading: boolean;
  mailOrder: boolean;
  mobileSearchDistance: number | undefined;
  page: number;
  pharmacyAnchorTarget: string;
  // pharmacies to display in the list
  pharmacyResults: PharmacyResultsType | undefined;
  plan?: Plan;
  previousNameSearch: string;
  searchDistance: number;
  showMap: boolean;
  showMobileFilter: boolean;
  shouldMailOrderBeInResults: boolean;
  totalPages: number;
  totalPharmacies: number;
  inNetworkFilterAnalytics?: (
    showPreferred: boolean,
    searchDistance: number
  ) => void;
};

export const ResultsContainer: FunctionComponent<ResultsContainerProps> = ({
  handlePreferredOnlyClick,
  isInNetwork,
  isMailOrderInNetwork,
  isMailOrderPreferred,
  loading,
  loadPharmacies,
  mailOrder,
  mobileSearchDistance,
  page,
  pharmacyAnchorTarget,
  pharmacyResults,
  plan,
  previousNameSearch,
  searchDistance,
  setFooterMessage,
  setMobileSearchDistance,
  setPage,
  setSearchDistance,
  setShowMap,
  setShowMobileFilter,
  showMap,
  showMobileFilter,
  shouldMailOrderBeInResults,
  toggleMailOrderPharmacy,
  togglePharmacy,
  totalPages,
  totalPharmacies,
  inNetworkFilterAnalytics,
}) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const flags = useFlags();
  const { mctCurrentPlanYear } = flags;
  const {
    state: { pharmacies },
    state,
    dispatch,
  } = useContext(AppContext);
  const distanceOptions = [
    {
      label: `${t("pharm_selection.distance")}: 1 ${t("pharm_selection.mile")}`,
      value: "1",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 5 }
      )}`,
      value: "5",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 10 }
      )}`,
      value: "10",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 25 }
      )}`,
      value: "25",
    },
  ];
  const [showOnlyPreferredInNetwork, setShowOnlyPreferredInNetwork] = useState(
    false
  );
  const [cachedMapRadius, setCachedMapRadius] = useState(0);
  const [drugCostInfo, setDrugCostsInfo] = useState<DrugCostInfo | null>(null);

  const handlePreferredToggle = () => {
    setShowOnlyPreferredInNetwork(!showOnlyPreferredInNetwork);
    handlePreferredOnlyClick(!showOnlyPreferredInNetwork);
  };

  const handleMapRadiusCaching = (radius: number) => {
    setCachedMapRadius(radius);
  };

  const mailOrderId = CONSTANTS.mailOrderNpi;
  const mailOrderNameId = `${mailOrderId}${nameIdSuffix}`;
  const mailOrderLabelId = `${mailOrderId}${labelIdSuffix}`;

  const filterRef = useRef<HTMLDivElement>(null);

  const scrollToFilterSection = () => {
    if (filterRef.current) {
      filterRef.current.scrollIntoView();
    }
  };

  const pharmacyNetworkLabel = isMailOrderInNetwork ? (
    <InNetworkLabel preferred={isMailOrderPreferred} />
  ) : (
    <OutOfNetworkLabel />
  );

  const pharmacyNetworkLabelHelpDrawer = (
    <PharmacyNetworkLabelHelpDrawer
      toggleText={
        <span className="ds-u-display--flex ds-u-align-items--center mct-u-white-space--nowrap">
          <span>
            <span className="ds-u-visibility--screen-reader">
              {t(
                "pharm_selection.in_network.show_only_preferred_in_network_pharmacies"
              )}
              :{" "}
            </span>
            {t("whats_this")}
          </span>
          <img src={InformationIcon} height="19" alt="" />
        </span>
      }
      toggleClassName="ds-u-margin-left--1 ds-u-margin-top--1"
      analyticText="What is this"
    />
  );

  useEffect(() => {
    (async () => {
      if (
        plan &&
        pharmacyResults?.pharmacies?.features &&
        pharmacyResults.pharmacies.features.length > 0
      ) {
        const geoPointPharmacies = pharmacyResults?.pharmacies.features.map(
          pharmacy => pharmacy.properties
        );
        const drugCosts = await fetchCosts({
          plan,
          t,
          state,
          mctCurrentPlanYear,
          pharmacies: geoPointPharmacies,
        });
        setDrugCostsInfo(drugCosts);
      }
    })();
  }, [plan, pharmacyResults?.pharmacies?.features]);

  const shouldShowDrugCosts = !!drugCostInfo && !!plan;

  const {
    state: { messages },
  } = useAlertMessaging();

  const messageToShow = getMessageToShow(
    messages,
    alertMessageHierarchy[AlertMessageHierarchyKeys.IN_NETWORK_PHARMACY_FINDER],
    t,
    { planType: plan?.plan_type }
  );

  return (
    <div className="SelectPharmaciesPage__resultsContainer">
      <div ref={filterRef} className="SelectPharmaciesPage__distanceFilter">
        <div className="ds-u-display--flex ds-u-lg-display--none ds-u-justify-content--center mct-u-width--full">
          <Button
            className="ds-u-margin-right--2 ds-u-display--flex ds-u-align-items--center"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: t("pharm_selection.filter"),
                  },
                },
              });
              setShowMobileFilter(!showMobileFilter);
            }}
          >
            <img src={filterIcon} alt="" />{" "}
            <span className="ds-u-margin-left--1">
              {t("pharm_selection.filter")}
            </span>
          </Button>
          <Button
            isAlternate
            variation="solid"
            className="ds-u-display--flex ds-u-align-items--center"
            onClick={(): void => {
              setShowMap(!showMap);
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans - Add Pharmacy",
                  event_label: `${showMap ? "Unhide" : "Hide"} Map`,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: !showMap
                      ? t("pharm_selection.show_map")
                      : t("pharm_selection.hide_map"),
                  },
                },
              });
            }}
          >
            <img src={mapIcon} alt="" />
            <span className="ds-u-margin-left--1">
              {!showMap
                ? t("pharm_selection.show_map")
                : t("pharm_selection.hide_map")}
            </span>
          </Button>
        </div>

        {showMobileFilter && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={t("pharm_selection.filters")}
              headingLevel="2"
              onCloseClick={(): void => {
                setShowMobileFilter(false);
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Find Plans - Add Pharmacy",
                    event_label: "Close Filters",
                  },
                });
              }}
              closeButtonText={<CloseIconThin />}
            >
              <>
                <ChoiceList
                  type="radio"
                  name="distance"
                  choices={distanceOptions.map(c => ({
                    ...c,
                    checked: c.value === mobileSearchDistance?.toString(),
                  }))}
                  onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
                    setMobileSearchDistance(Number(e.currentTarget.value));
                  }}
                  label={t("pharm_selection.distance")}
                />
                {isInNetwork && (
                  <>
                    <Choice
                      name="show-only-preferred-in-network-pharmacies-choice"
                      type="checkbox"
                      value="show-only-preferred"
                      checked={showOnlyPreferredInNetwork}
                      onChange={() => {
                        setShowOnlyPreferredInNetwork(
                          !showOnlyPreferredInNetwork
                        );
                      }}
                      label={t(
                        "pharm_selection.in_network.show_only_preferred_in_network_pharmacies"
                      )}
                    />
                    {pharmacyNetworkLabelHelpDrawer}
                  </>
                )}
                <div className="SelectPharmaciesPage__mobileFilterActions">
                  <Button
                    isAlternate
                    variation="solid"
                    onClick={(): void => {
                      if (mobileSearchDistance) {
                        setSearchDistance(mobileSearchDistance);
                        loadPharmacies({
                          radius_miles: mobileSearchDistance,
                          pageNumber: 0,
                          filterByPreferred: showOnlyPreferredInNetwork,
                        });
                        if (inNetworkFilterAnalytics) {
                          inNetworkFilterAnalytics(
                            showOnlyPreferredInNetwork,
                            mobileSearchDistance
                          );
                        }
                      }
                      dispatch({
                        type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                        settings: {
                          button: {
                            buttonStyle: AnalyticsButtonStyle.SECONDARY,
                            buttonType: AnalyticsButtonType.BUTTON,
                            text: t("pharm_selection.apply"),
                          },
                        },
                      });

                      setShowMobileFilter(false);
                      scrollToFilterSection();
                    }}
                  >
                    {t("pharm_selection.apply")}
                  </Button>
                </div>
              </>
            </HelpDrawer>
          </div>
        )}
        <div className="ds-u-display--none ds-u-lg-display--flex ds-u-align-items--center">
          <p className="ds-u-margin-right--2" id="filter-by-text">
            {t("pharm_selection.filter_by")}:
          </p>
          <CCXPDropdown
            value={searchDistance.toString()}
            name="filter-by"
            aria-labelledby="filter-by-text"
            label={t("pharm_selection.distance")}
            options={distanceOptions}
            onUpdate={(value: string): void => {
              const dist = Number(value);
              setSearchDistance(dist);
              loadPharmacies({
                radius_miles: dist,
                pageNumber: 0,
                filterByPreferred: showOnlyPreferredInNetwork,
              });
              if (!isInNetwork) {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: "Find Plans - Add Pharmacy",
                    event_label: `Results UI: Filter by: ${dist} miles`,
                  },
                });
              } else if (inNetworkFilterAnalytics) {
                inNetworkFilterAnalytics(showOnlyPreferredInNetwork, dist);
              }
            }}
            hideClear
          />
          {isInNetwork && (
            <>
              <Choice
                className="ds-u-margin-left--3"
                name="show-only-preferred-in-network-pharmacies-choice"
                type="checkbox"
                value="show-only-preferred"
                checked={showOnlyPreferredInNetwork}
                onChange={handlePreferredToggle}
                label={t(
                  "pharm_selection.in_network.show_only_preferred_in_network_pharmacies"
                )}
              />
              {pharmacyNetworkLabelHelpDrawer}
            </>
          )}
        </div>
      </div>
      <div className="SelectPharmaciesPage__results">
        <div className="SelectPharmaciesPage__pharmacyOptionsContainer">
          {isInNetwork && messageToShow && (
            <AlertMessage
              className="SelectPharmaciesPage__alertMessage"
              {...{
                icon: messageToShow.icon,
                body: messageToShow.body,
                action: messageToShow.action,
                title: messageToShow.title,
                condition: messageToShow.key,
              }}
            />
          )}
          <FormattedResultsMessage
            {...{
              pharmacyResults,
              totalPharmacies,
              previousNameSearch,
              page,
            }}
          />
          {shouldMailOrderBeInResults && (
            <div
              className="SelectPharmaciesPage__grayBottomBorder ds-u-padding-y--1"
              id="MailOrderPharmacyCard"
            >
              <div className="SelectPharmaciesPage__pharmacyCard">
                <div className="ds-u-display--flex">
                  {pharmacyResults &&
                    pharmacyResults.pharmacies.features.length > 0 && (
                      <span className="SelectPharmaciesPage__pharmacyNumber">
                        &nbsp;
                      </span>
                    )}
                  <div className="SelectPharmaciesPage__pharmacyInfo">
                    <h2
                      id={mailOrderNameId}
                      className={`SelectPharmaciesPage__pharmacyCardHeader ${isInNetwork &&
                        "ds-u-margin-bottom--0"}`}
                    >
                      {t("pharm_selection.mail_order_pharm")}
                    </h2>
                    <p className="ds-u-margin--0 SelectPharmaciesPage__pharmacyCard__status-container">
                      {isInNetwork && pharmacyNetworkLabel}
                    </p>
                    <p className="ds-u-margin--0">
                      {t("pharm_selection.mail_order_pharm_desc")}
                    </p>
                  </div>
                </div>

                <div
                  className={classNames(baseAddPharmacyClassName, {
                    [`${baseAddPharmacyClassName}--with-costs`]: shouldShowDrugCosts,
                    [`${baseAddPharmacyClassName}--without-costs`]: !shouldShowDrugCosts,
                  })}
                >
                  {shouldShowDrugCosts && (
                    <div className="SelectPharmaciesPage__pharmacyDrugCosts">
                      {/* 
                        Even though the boolean should guarantee drugCostInfo and plan
                        are defined, compile-time throws errors without casting the types
                      */}
                      <PharmacyDrugCosts
                        drugCosts={drugCostInfo as DrugCostInfo}
                        plan={plan as Plan}
                        npi={CONSTANTS.mailOrderNpi}
                      />
                    </div>
                  )}
                  <Choice
                    type="checkbox"
                    name="include-mail-order-pharmacy"
                    value="mail-order-pharmacy"
                    className="e2e-mail-order-pharmacy SelectPharmaciesPage__pharmaciesOption"
                    checked={mailOrder}
                    onChange={(): void => {
                      if (
                        !mailOrder &&
                        pharmacies.length >= CONSTANTS.pharmacySelection.max
                      ) {
                        setFooterMessage(
                          fm(
                            {
                              id:
                                "pharmacy_selection_page.max_selection_warning",
                            },
                            { count: CONSTANTS.pharmacySelection.max }
                          )
                        );
                      } else {
                        toggleMailOrderPharmacy(!mailOrder);
                      }
                    }}
                    aria-labelledby={`${mailOrderNameId} ${mailOrderLabelId}`}
                  >
                    <span id={mailOrderLabelId}>
                      {/* Add sr-only comma and space for screen-reader intelligibility */}
                      <span className="ds-u-visibility--screen-reader">{`, `}</span>
                      {mailOrder
                        ? t("pharm_selection.pharmacy_added")
                        : t("pharm_selection.add_pharmacy")}
                    </span>
                  </Choice>
                </div>

                {!!pharmacies.length && (
                  <SkipNav
                    href={`#${pharmacyAnchorTarget}`}
                    onClick={(e): void => {
                      e.preventDefault();
                      document.getElementById(pharmacyAnchorTarget)?.focus();
                    }}
                  >
                    {t("pharmacy_skip_text")}
                  </SkipNav>
                )}
              </div>
            </div>
          )}

          {!loading &&
            pharmacyResults &&
            !pharmacyResults.pharmacies.features.length && (
              <div className="ds-u-padding-y--3 ds-u-padding-left--2 ds-u-md-padding-left--7 ds-u-md-padding-right--3">
                <h2>
                  {isInNetwork
                    ? showOnlyPreferredInNetwork
                      ? t("pharm_selection.in_network.no_preferred_plans_found")
                      : t(
                          "pharm_selection.in_network.no_in_network_plans_found"
                        )
                    : t("pharm_selection.no_pharmacies")}
                </h2>
                <p>{t("pharm_selection.improve_search")}</p>
                <ul className="ds-c-list--bare" role="list">
                  <li role="listitem">
                    <p>{t("pharm_selection.try_different_area")}</p>
                  </li>
                  <li role="listitem">
                    <p>{t("pharm_selection.check_spelling")}</p>
                  </li>
                  {showOnlyPreferredInNetwork && (
                    <li role="listitem">
                      <Button
                        className="ds-u-display--block ds-u-margin-top--1"
                        onClick={handlePreferredToggle}
                      >
                        {t(
                          "pharm_selection.in_network.show_standard_in_network_pharmacies"
                        )}
                      </Button>
                    </li>
                  )}
                </ul>
              </div>
            )}

          {pharmacyResults && (
            <ol className="SelectPharmaciesPage__pharmacies" role="list">
              {pharmacyResults.pharmacies.features.map((pharmacy, index) => {
                const { properties } = pharmacy;
                const {
                  npi,
                  name,
                  phone,
                  city,
                  state,
                  street,
                  unit,
                  zipcode,
                } = properties;

                const address = `${normalizeStringCase(street)}${
                  unit ? ` ${normalizeStringCase(unit)}` : ""
                }, ${normalizeStringCase(city)}, ${state} ${zipcode.substring(
                  0,
                  5
                )}`;

                const isSelected = pharmacies.map(p => p.npi).includes(npi);

                const pharmacyNumber =
                  CONSTANTS.pharmacySelection.pageCount * page + (index + 1);

                const pharmacyCardProps: PharmacyCardProps = {
                  address,
                  npi,
                  name,
                  phone,
                  pharmacyNumber,
                  pharmacyAnchorTarget,
                  handleToggle: togglePharmacy,
                  preferred: properties.preferred || false,
                  shouldShowSkipNav: !!pharmacies.length,
                  shouldShowNetworkStatus: isInNetwork,
                  index,
                  inNetwork: properties.in_network || false,
                  isSelected,
                  pharmacyDrugCosts: shouldShowDrugCosts ? (
                    <PharmacyDrugCosts
                      drugCosts={drugCostInfo as DrugCostInfo}
                      plan={plan as Plan}
                      npi={npi}
                    />
                  ) : (
                    undefined
                  ),
                };
                return <PharmacyCard key={npi} {...pharmacyCardProps} />;
              })}
            </ol>
          )}
          {!loading &&
            pharmacyResults &&
            pharmacyResults.pharmacies.features.length > 0 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalResults={pharmacyResults.totalResults}
                onPageSelect={(page: number): void => {
                  setPage(page);
                  loadPharmacies({
                    radius_miles: searchDistance,
                    pageNumber: page,
                    filterByPreferred: showOnlyPreferredInNetwork,
                  });
                }}
              />
            )}
        </div>
        {pharmacyResults && showMap && (
          <div className="SelectPharmaciesPage__pharmaciesMap">
            <PharmacySelectionMap
              data={pharmacyResults}
              selectedPharmacies={pharmacies}
              page={page}
              onRadiusCache={handleMapRadiusCaching}
              fallbackRadius={cachedMapRadius}
            />
          </div>
        )}
      </div>
    </div>
  );
};
